import { ApeironBlueButton, SkeletonTypography } from '@apeiron/temp'
import { Box, styled } from '@mui/material'
import PlanetOnTrackDatagrid from '@src/components/orbitalTrack/detail/PlanetOnTrackDatagrid'
import { useOrbitalTrackAttachContext } from '@src/contexts/orbitalTrack/OrbitalTrackAttachContext'
import useOrbitalTrackHelper from '@src/hooks/apeiron/orbitalTrack/useOrbitalTrackHelper'
import { OrbitalTrackLite } from '@src/types/orbitalTrack'
import * as R from 'ramda'
import { FC } from 'react'
import { Trans, useTranslation } from 'react-i18next'

const Container = styled(Box)`
  background: #333536;
  border-radius: 1px;
  display: flex;
  flex-direction: column;
  padding: 16px;
  width: 100%;
  ${props => props.theme.breakpoints.up('res480')} {
    gap: 16px;
  }
  ${props => props.theme.breakpoints.down('res480')} {
    gap: 8px;
  }
`

const Heading = styled(Box)`
  align-items: center;
  display: flex;
  gap: 5px;
  justify-content: space-between;
  width: 100%;
`

const TrackNumber = styled(SkeletonTypography)`
  flex: 1;
  font-weight: 400;
  height: auto;
  white-space: pre-wrap;
  ${props => props.theme.breakpoints.up('res1280')} {
    font-size: 16px;
  }
  ${props => props.theme.breakpoints.down('res1280')} {
    font-size: 14px;
  }
  strong {
    color: #8ee161;
  }
`

const AttachPlanetButton = styled(ApeironBlueButton)`
  background-color: unset;
  border-radius: 4px;
  border: 2px solid #1d66bf;
  color: white;
  margin-left: auto;
  text-transform: none;
  :focus {
    background-color: unset;
  }
  :hover {
    background-color: unset;
  }
  :disabled {
    background-color: unset;
    opacity: 0.3;
  }
  ${props => props.theme.breakpoints.up('res1280')} {
    width: 160px;
  }
  ${props => props.theme.breakpoints.down('res1280')} {
    font-size: 14px;
    width: 120px;
  }
  ${props => props.theme.breakpoints.down('res480')} {
    width: 110px;
  }
`

const StyledPlanetOnTrackDatagrid = styled(PlanetOnTrackDatagrid)`
  height: 300px;
`

const PlanetOnTrackSection: FC<Props> = props => {
  const { className, onAttach } = props

  const { loading, trackDetail } = useOrbitalTrackAttachContext()

  const { checkInCooldown } = useOrbitalTrackHelper()

  const ownerNoCDData = R.reject(
    (track: OrbitalTrackLite) => checkInCooldown(track),
    R.pathOr([], ['ownerTracks'], trackDetail)
  )

  const { t } = useTranslation()

  const trackLength = trackDetail?.ownerTracks?.length || 0

  return (
    <Container className={className}>
      <Heading>
        <TrackNumber loading={loading}>
          <Trans>
            {t('godiverse.orbital_track.planet', {
              total: R.isNotNil(ownerNoCDData) ? ownerNoCDData.length : '-',
              cd: R.isNotNil(ownerNoCDData)
                ? trackLength - ownerNoCDData.length
                : '-'
            })}
          </Trans>
        </TrackNumber>
        <AttachPlanetButton
          disabled={
            loading ||
            R.isNil(trackDetail) ||
            !trackDetail.availableOrbitalTrackCount ||
            trackDetail.star?.sale
          }
          disableReason={t('orbit.block_on_misc_reasons')}
          onClick={onAttach}
        >
          {t('godiverse.orbital_track.button.attach_planet')}
        </AttachPlanetButton>
      </Heading>
      <StyledPlanetOnTrackDatagrid />
    </Container>
  )
}

type Props = {
  className?: string
  onAttach: () => void
}
export default PlanetOnTrackSection
